import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {TFunction, useTranslation} from 'react-i18next';

import {ReactComponent as AttentionIcon} from 'assets/svg/attention.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Modal} from 'components/ui-kit/Modal';

import {
    setUpdateParticipantRolesModalVisible,
    updateParticipantRolesFx,
    updateParticipantRolesModalVisible$,
} from 'models/profile/common';
import {$updateParticipantRolesPending} from 'models/profile/common/init';
import {colors} from 'theme';

import {ProfileRole} from 'types/profile';

import {
    CustomList,
    CustomTitle,
    CustomTitleIcon,
    CustomTitleText,
    CustomTitleWrapper,
    Footer,
    HeadlineH2,
    Root,
    Wrapper,
    WrapperItem,
} from './styled';

const Title = ({t}: {t: TFunction}) => (
    <CustomTitle>
        <CustomTitleIcon>
            <Icon
                width={20}
                height={20}
                source={AttentionIcon}
                strokeColor={colors.danger}
                fillColor={colors.dangerSecondary}
            />
        </CustomTitleIcon>
        <CustomTitleWrapper>
            <CustomTitleText size="md">{t('pages.profile.common.modal.title')}</CustomTitleText>
            <CustomTitleText size="sm">
                {t('pages.profile.common.modal.description')}
            </CustomTitleText>
        </CustomTitleWrapper>
    </CustomTitle>
);

const CustomFooter = ({
    onClose,
    legalIdentityId,
    data,
    isPending,
    t,
}: {
    onClose: () => void;
    legalIdentityId?: string | number;
    data: {add: ProfileRole[]; remove: ProfileRole[]};
    isPending: boolean;
    t: TFunction;
}) => (
    <Footer>
        <div>
            <Button variant={ButtonVariant.Outline} onClick={onClose} disabled={isPending}>
                {t('pages.profile.common.modal.buttons.cancel')}
            </Button>
        </div>
        <div>
            <Button
                isLoading={isPending}
                variant={ButtonVariant.Normal}
                onClick={() => {
                    if (!legalIdentityId) return;
                    const {add, remove} = data;
                    updateParticipantRolesFx({
                        legalIdentityId,
                        body: {
                            ...(add.length && {rolesToAdd: add}),
                            ...(remove.length && {rolesToDeactivate: remove}),
                        },
                    });
                }}
            >
                {t('pages.profile.common.modal.buttons.confirm')}
            </Button>
        </div>
    </Footer>
);

export const TypeConfirmationModal = ({
    data,
    legalIdentityId,
}: {
    data: {add: ProfileRole[]; remove: ProfileRole[]};
    legalIdentityId?: string | number;
}) => {
    const {t} = useTranslation();
    const updateParticipantRolesPending = useStore($updateParticipantRolesPending);
    const updateParticipantRolesModalVisible = useStore(updateParticipantRolesModalVisible$);
    const onClose = () => setUpdateParticipantRolesModalVisible(false);

    return (
        <Modal
            closable={true}
            show={updateParticipantRolesModalVisible}
            onClose={onClose}
            hideCloseButton={false}
            modalWidth={1}
            titleNoPadding={true}
            footer={
                <CustomFooter
                    onClose={onClose}
                    legalIdentityId={legalIdentityId}
                    data={data}
                    isPending={updateParticipantRolesPending}
                    t={t}
                />
            }
            title={<Title t={t} />}
        >
            <Root>
                <Wrapper>
                    {data.remove.length > 0 && (
                        <WrapperItem>
                            <HeadlineH2>{t('pages.profile.common.modal.removed')}</HeadlineH2>
                            <CustomList action="remove">
                                {data.remove.map((item) => (
                                    <li key={item}>{t(`dictionaries.profileRole.${item}`)}</li>
                                ))}
                            </CustomList>
                        </WrapperItem>
                    )}
                    {data.add.length > 0 && (
                        <WrapperItem>
                            <HeadlineH2>{t('pages.profile.common.modal.added')}</HeadlineH2>
                            <CustomList action="add">
                                {data.add.map((item) => (
                                    <li key={item}>{t(`dictionaries.profileRole.${item}`)}</li>
                                ))}
                            </CustomList>
                        </WrapperItem>
                    )}
                </Wrapper>
            </Root>
        </Modal>
    );
};
